import React, { useCallback } from 'react';

import { useNarrativeTextInput } from '../../hooks/useNarrativeTextInput';
import { logger } from '../../lib/logger';
import {
  useCurrentNode,
  useNarrativeState,
  useUpdateNode,
  useUpdateNodeChoice,
} from '../../store/hooks';
import { shouldShowVariantTable } from '../../utils/variantTableHelpers';

import { ChoicesList } from './ChoicesList';
import { DynamicTextarea } from './DynamicTextarea';
import { SectionTitle } from './SectionTitle';

const standardLogger = logger.child('[StandardNodeEditor]');

export const StandardNodeEditor = React.memo(function StandardNodeEditor() {
  const narrativeState = useNarrativeState();
  const currentNode = useCurrentNode();
  const updateNode = useUpdateNode();
  const updateNodeChoice = useUpdateNodeChoice();
  const currentNodeId = narrativeState.currentNodeId;

  const handleTextUpdate = useCallback(
    (value: string) => {
      updateNode(currentNodeId, { text: value });
    },
    [currentNodeId, updateNode]
  );

  const mainText = useNarrativeTextInput(currentNode?.text || '', handleTextUpdate, 300, 1000);

  const handleChoiceUpdate = useCallback(
    (choiceIndex: number, field: 'text' | 'npcResponse', value: string) => {
      updateNodeChoice(currentNodeId, choiceIndex, { [field]: value });
    },
    [currentNodeId, updateNodeChoice]
  );

  // Check if all choices are filled but main text is empty
  const shouldHighlightMainText = React.useMemo(() => {
    if (!currentNode || !currentNode.choices) return false;

    const mainTextEmpty = !mainText.value || mainText.value.trim() === '';
    if (!mainTextEmpty) return false;

    // Check if all choice fields are filled
    const allChoicesFilled = currentNode.choices.every((choice) => {
      const hasChoiceText = choice.text && choice.text.trim() !== '';
      const hasNpcResponse =
        choice.npcResponse !== undefined
          ? choice.npcResponse && choice.npcResponse.trim() !== ''
          : true;

      // Handle variable content for specific nodes
      const hasVariableContent = choice.setsVariable?.value
        ? typeof choice.setsVariable.value === 'object'
          ? choice.setsVariable.value.playerFacingText?.trim() !== '' &&
            choice.setsVariable.value.memoryContent?.trim() !== ''
          : true
        : true;

      return hasChoiceText && hasNpcResponse && hasVariableContent;
    });

    return allChoicesFilled && currentNode.choices.length > 0;
  }, [currentNode, mainText.value]);

  standardLogger.debug('Rendered');

  if (!currentNode) {
    return null;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Main Text Section - Fixed Size */}
      <div className="flex flex-col">
        <SectionTitle
          title="Text Leading Up to Choices"
          subtitle={
            currentNode.id === 'S1_Opening_Hook'
              ? 'Opening Scene'
              : shouldShowVariantTable(currentNode.id)
                ? 'This node will start with one of several variant lines (see the table opposite). Write text below that could follow from any of those starting points.'
                : undefined
          }
          text={mainText.value}
          showWordCount={true}
          isWarning={shouldHighlightMainText}
          className="flex-shrink-0"
        />
        <DynamicTextarea
          value={mainText.value}
          onChange={(e) => mainText.onChange(e.target.value)}
          className={`w-full bg-gradient-to-br from-[#161c2e] to-[#1a2040] border rounded-lg p-4 text-white focus:outline-none focus:border-[#4f46e5] focus:shadow-[0_0_20px_rgba(79,70,229,0.3)] focus:scale-[1.01] hover:border-[#06b6d4] transition-all duration-300 text-sm backdrop-blur-sm ${
            shouldHighlightMainText
              ? 'border-[#ffcc00] shadow-lg shadow-[#ffcc00]/20 animate-pulse'
              : 'border-[#2a3756] shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
          }`}
          placeholder={
            shouldHighlightMainText
              ? '⚠️ Main text is missing - write your text here...'
              : 'Write your text here...'
          }
          minRows={5}
          wordLimit={100}
        />
      </div>

      {/* Choices Section */}
      <div>
        <ChoicesList
          choices={currentNode.choices}
          currentNodeId={currentNodeId}
          onChoiceUpdate={handleChoiceUpdate}
        />
      </div>
    </div>
  );
});
